/**
 * ChatListHeader.tsx
 *
 * Header for chat list with search input and tab toggle (myChat/allChat).
 * Extracted from ChatWrapper.v2.tsx.
 */

import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { KTSVG } from "src/resources/helpers/components/KTSVG";
import * as chatV2 from "src/app/modules/chat/redux/ChatSlice.v2";
import { ChatType } from "src/app/modules/chat/feature";

type ChatListHeaderProps = {
  activeTab: "allChat" | "myChat";
  filter: string;
  onTabChange: (tab: "myChat" | "allChat") => void;
  isMobile?: boolean;
  mobileSearch?: boolean;
  onToggleMobileSearch?: () => void;
};

const ChatListHeader: FC<ChatListHeaderProps> = ({
  activeTab,
  filter,
  onTabChange,
  isMobile = false,
  mobileSearch = false,
  onToggleMobileSearch,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const bgColorClassActive = "bg-light-success";
  const textColorClassActive = "text-success";
  const bgColorClassInActive = "bg-secondary";
  const textColorClassInActive = "text-muted";

  const handleFilterChange = (value: string) => {
    dispatch(chatV2.setFilter(value));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.keyCode === 13) {
      e.preventDefault();
      return false;
    }
  };

  // Desktop layout
  if (!isMobile) {
    return (
      <div
        className="d-flex flex-column"
        id="kt_chat_contacts_header_parent"
        style={{
          height: "90px",
          flexShrink: 0, // Prevent header from shrinking
          borderStyle: "solid",
          borderTopWidth: 0,
          borderLeftWidth: 0,
          borderRightWidth: 0,
          borderBottomWidth: "0.1px",
          borderColor: "rgba(161, 165, 183, 0.5)",
        }}
      >
        {/* Search input */}
        <div className="p-0 mx-2 mt-2 mb-1" id="kt_chat_contacts_header">
          <form className="w-100 m-0 p-0 position-relative" autoComplete="off">
            <div className="position-relative p-0 m-0">
              <KTSVG
                path="/media/icons/duotune/general/gen021.svg"
                className="svg-icon-5 svg-icon-gray-500 position-absolute top-50 start-0 translate-middle-y ms-2"
              />
              <input
                id="search-chat"
                type="text"
                className="form-control form-control-solid form-control-sm py-2 m-0 bg-light-secondary ps-8"
                name="search"
                placeholder={t("HC.Input.Search")}
                onChange={(e) => handleFilterChange(e.target.value)}
                value={filter}
                onKeyDown={handleKeyDown}
                style={{ fontSize: "13px" }}
              />
            </div>
          </form>
        </div>

        {/* Tab toggle */}
        <div
          className="d-flex flex-row align-items-center justify-content-start mx-2 mb-1 col-gap-1"
          style={{ width: "auto" }}
        >
          <div
            onClick={() => onTabChange("myChat")}
            className={`cursor-pointer rounded-pill d-flex flex-row align-items-center justify-content-center py-1 px-3 ${
              activeTab === ChatType.mychat
                ? bgColorClassActive
                : bgColorClassInActive
            }`}
            style={{
              borderStyle: "solid",
              borderWidth: "0.5px",
              borderColor:
                activeTab === ChatType.mychat ? "#50CD89" : "#A1A5B7",
            }}
          >
            <span
              className={`p-0 m-0 fw-semibold ${
                activeTab === ChatType.mychat
                  ? textColorClassActive
                  : textColorClassInActive
              }`}
              style={{ fontSize: "12px" }}
            >
              {t("HC.Button.MyChats")}
            </span>
          </div>
          <div
            onClick={() => onTabChange("allChat")}
            className={`cursor-pointer rounded-pill d-flex flex-row align-items-center justify-content-center py-1 px-3 ${
              activeTab === ChatType.allchat
                ? bgColorClassActive
                : bgColorClassInActive
            }`}
            style={{
              borderStyle: "solid",
              borderWidth: "0.5px",
              borderColor:
                activeTab === ChatType.allchat ? "#50CD89" : "#A1A5B7",
            }}
          >
            <span
              className={`p-0 m-0 fw-semibold ${
                activeTab === ChatType.allchat
                  ? textColorClassActive
                  : textColorClassInActive
              }`}
              style={{ fontSize: "12px" }}
            >
              {t("HC.Button.AllChats")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Mobile layout
  return (
    <div
      className="d-flex flex-column"
      id="kt_chat_contacts_header_parent"
      style={{
        flexShrink: 0, // Prevent header from shrinking
        borderStyle: "solid",
        borderTopWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: "0.1px",
        borderColor: "rgba(161, 165, 183, 0.5)",
      }}
    >
      {/* Search input (conditionally shown) */}
      <div className="p-0 m-0 mx-2" id="kt_chat_contacts_header">
        {mobileSearch && (
          <form className="w-100 m-0 p-0 position-relative" autoComplete="off">
            <div className="position-relative p-0 m-0 mt-1">
              <input
                id="search-chat"
                type="text"
                className="form-control form-control-solid form-control-sm py-2 m-0 bg-light-secondary ps-3"
                name="search"
                placeholder={t("HC.Input.Search")}
                onChange={(e) => handleFilterChange(e.target.value)}
                value={filter}
                onKeyDown={handleKeyDown}
                style={{ fontSize: "13px", borderRadius: 200 }}
              />
            </div>
          </form>
        )}
      </div>

      {/* Tab toggle with search icon */}
      <div className="d-flex flex-row align-items-center justify-content-between mx-2">
        <div className="d-flex flex-row align-items-center justify-content-start my-1 col-gap-1">
          <div
            onClick={() => onTabChange("myChat")}
            className={`cursor-pointer rounded-pill d-flex flex-row align-items-center justify-content-center py-1 px-3 ${
              activeTab === ChatType.mychat
                ? bgColorClassActive
                : bgColorClassInActive
            }`}
            style={{
              borderStyle: "solid",
              borderWidth: "0.5px",
              borderColor:
                activeTab === ChatType.mychat ? "#50CD89" : "#A1A5B7",
            }}
          >
            <span
              className={`p-0 m-0 fw-semibold ${
                activeTab === ChatType.mychat
                  ? textColorClassActive
                  : textColorClassInActive
              }`}
              style={{ fontSize: "12px" }}
            >
              {t("HC.Button.MyChats")}
            </span>
          </div>
          <div
            onClick={() => onTabChange("allChat")}
            className={`cursor-pointer rounded-pill d-flex flex-row align-items-center justify-content-center py-1 px-3 ${
              activeTab === ChatType.allchat
                ? bgColorClassActive
                : bgColorClassInActive
            }`}
            style={{
              borderStyle: "solid",
              borderWidth: "0.5px",
              borderColor:
                activeTab === ChatType.allchat ? "#50CD89" : "#A1A5B7",
            }}
          >
            <span
              className={`p-0 m-0 fw-semibold ${
                activeTab === ChatType.allchat
                  ? textColorClassActive
                  : textColorClassInActive
              }`}
              style={{ fontSize: "12px" }}
            >
              {t("HC.Button.AllChats")}
            </span>
          </div>
        </div>
        <svg
          onClick={onToggleMobileSearch}
          className="me-2 cursor-pointer"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 42 42"
        >
          <path
            fill="#a1a5b7"
            d="M1 17.838c0 8.747 7.131 15.827 15.94 15.827c8.796 0 15.938-7.08 15.938-15.827S25.736 2 16.94 2C8.131 2 1 9.091 1 17.838zm5.051 0c0-5.979 4.868-10.817 10.89-10.817c6.01 0 10.888 4.839 10.888 10.817c0 5.979-4.878 10.818-10.888 10.818c-6.022 0-10.89-4.84-10.89-10.818zm22.111 14.523l6.855 7.809c1.104 1.102 1.816 1.111 2.938 0l2.201-2.181c1.082-1.081 1.149-1.778 0-2.921l-7.896-6.775l-4.098 4.068z"
          />
        </svg>
      </div>
    </div>
  );
};

export default ChatListHeader;
